import {Howl, Howler} from 'howler';

/* FUNCTIONS:
 * - debouncer(func, timeout)
 * - documentReady(callbackFunction)
 * - slideUp(target, duration=500)
 * - slideDown(target, duration=500)
 * - slideToggle(target, duration = 500)
 * - index(el)
 * - scrollToEl(targetEl)
 * - staggerText(targetEl)
 * - triggerEvent(el, type)
 * - playAudioSync(el, withAudio)
 * - playBGMusic()
 * - pauseBGMusic()
 */

// used for window resize/scroll functions to reduce function calls
export function debouncer(func, timeout) {
  var timeoutID,
    timeout = timeout || 20;
  return function () {
    var scope = this,
      args = arguments;
    clearTimeout(timeoutID);
    timeoutID = setTimeout(function () {
      func.apply(scope, Array.prototype.slice.call(args));
    }, timeout);
  };
}

export function throttle(fn, wait) {
  let time = Date.now();
  return function (e) {
    if (time + wait - Date.now() < 0) {
      fn(e);
      time = Date.now();
    }
  };
}

// Document ready function
export const documentReady = (callbackFunction) => {
  // if(document.readyState != 'loading')
  //   callbackFunction(event)
  // else
  // document.addEventListener("DOMContentLoaded", callbackFunction);
  // turbo.js
  document.addEventListener("turbo:load", callbackFunction); // Fires on page load
}

/* SLIDE UP */
export function slideUp(target, duration = 500) {
  target.style.transitionProperty = "height, margin, padding";
  target.style.transitionDuration = duration + "ms";
  target.style.boxSizing = "border-box";
  target.style.height = target.offsetHeight + "px";
  target.offsetHeight;
  target.style.overflow = "hidden";
  target.style.height = 0;
  target.style.paddingTop = 0;
  target.style.paddingBottom = 0;
  target.style.marginTop = 0;
  target.style.marginBottom = 0;
  window.setTimeout(() => {
    target.style.display = "none";
    target.style.removeProperty("height");
    target.style.removeProperty("padding-top");
    target.style.removeProperty("padding-bottom");
    target.style.removeProperty("margin-top");
    target.style.removeProperty("margin-bottom");
    target.style.removeProperty("overflow");
    target.style.removeProperty("transition-duration");
    target.style.removeProperty("transition-property");
    target.style.removeProperty("box-sizing");
    //alert("!");
  }, duration);
}

/* SLIDE DOWN */
export function slideDown(target, duration = 500) {
  target.style.removeProperty("display");
  let display = window.getComputedStyle(target).display;
  if (display === "none") display = "block";
  target.style.display = display;
  let height = target.offsetHeight;
  target.style.overflow = "hidden";
  target.style.height = 0;
  target.style.paddingTop = 0;
  target.style.paddingBottom = 0;
  target.style.marginTop = 0;
  target.style.marginBottom = 0;
  target.offsetHeight;
  target.style.boxSizing = "border-box";
  target.style.transitionProperty = "height, margin, padding";
  target.style.transitionDuration = duration + "ms";
  target.style.height = height + "px";
  target.style.removeProperty("padding-top");
  target.style.removeProperty("padding-bottom");
  target.style.removeProperty("margin-top");
  target.style.removeProperty("margin-bottom");
  window.setTimeout(() => {
    target.style.removeProperty("height");
    target.style.removeProperty("overflow");
    target.style.removeProperty("transition-duration");
    target.style.removeProperty("transition-property");
    target.style.removeProperty("box-sizing");
  }, duration);
}

/* SLIDE TOGGLE */
export function slideToggle(target, duration = 500) {
  if (window.getComputedStyle(target).display === "none") {
    return slideDown(target, duration);
  } else {
    return slideUp(target, duration);
  }
}

// Return index of el
export function index(el) {
  if (!el) return -1;
  var i = 0;
  do {
    i++;
  } while ((el = el.previousElementSibling));
  return i;
}

// Smooth scroll to element
export function scrollToEl(targetEl) {
  const currentScroll = window.pageYOffset | document.body.scrollTop;
  const targetPos = Math.round(targetEl.getBoundingClientRect().top);
  const adminBarEl = document.getElementById("wpadminbar");
  const adminBarHeight = !!adminBarEl ? adminBarEl.offsetHeight : 0;
  const scrollPos = targetPos - adminBarHeight + currentScroll - 20;
  scroll({
    top: scrollPos,
    behavior: "smooth",
  });
}

// Easier method of triggering events.
export function triggerEvent(el, type) {
  if ("createEvent" in document) {
    // modern browsers, IE9+
    var e = document.createEvent("HTMLEvents");
    e.initEvent(type, false, true);
    el.dispatchEvent(e);
  } else {
    // IE 8
    var e = document.createEventObject();
    e.eventType = type;
    el.fireEvent("on" + e.eventType, e);
  }
}

// Function to play audio sync elements
export function playAudioSync(el) {
  const audio = el.querySelector(".audioSync__audio audio");

  el.classList.add("audio_triggered");

  // Start the naration
  try {
    audio.play();
  } catch (e) {

  }

  // handle text highlight syncing
  const paragraphs = el.querySelectorAll(".audioSync__text");
  if (paragraphs) {
    paragraphs.forEach((p) => {
      const startTime = parseFloat(p.getAttribute("data-start"));
      const endTime = parseFloat(p.getAttribute("data-end"));
      const duration = endTime - startTime;
      const words = p.querySelectorAll(".audioSync__word");
      let timeStamp = startTime;
      let index = 0;
      let durationLeft = duration; // used to help calculate delays
      let word;
      let wordsLeft = words.length;
      let highlightTimes = [];

      // Figure out and set the highlight time for each word, to check against when the audio is played.
      for (var i = 0; i < words.length; i++) {
        index++;
        word = words[i];

        // Check for delay, and update the current timeStamp
        delay = word.getAttribute("data-delay") ? parseFloat(word.getAttribute("data-delay")) : 0;
        let thisTimeStamp = timeStamp + delay;

        // Set data-attribute to be used later
        highlightTimes.push(thisTimeStamp);

        // Increase the timestamp based on the word count remaining and duration remaining
        durationLeft = duration - (thisTimeStamp - startTime) - delay;
        durationLeft = durationLeft < 0 ? 0.01 : durationLeft;
        wordsLeft = words.length - index;

        timeStamp = thisTimeStamp + durationLeft / wordsLeft;
      }

      // Watch the timeupdate on the audio playback and check playback time against words highlight times.
      audio.addEventListener("timeupdate", (e) => {
        for (var i = 0; i < words.length; i++) {
          const word = words[i];
          const highlightTime = highlightTimes[i];
          if (highlightTime <= e.srcElement.currentTime && !word.classList.contains("highlighted")) {
            word.classList.add("highlighted");
          } else if (highlightTime > e.srcElement.currentTime && word.classList.contains("highlighted")) {
            word.classList.remove("highlighted"); // allows for rewinding
          }
        }
      });
    });
  }
}

// Background site music
export function playBGMusic(){
  const bg_audio_trigger = document.querySelector(".site_audio__trigger");

  if(!bg_audio_trigger.classList.contains('active')){
    bg_audio_trigger.classList.add("active");

    if (window.backgroundMusic) {
      window.backgroundMusic.play();
    }
  }
}
export function pauseBGMusic(){
  const bg_audio_trigger = document.querySelector(".site_audio__trigger");
  if(bg_audio_trigger.classList.contains('active')){
    bg_audio_trigger.classList.remove("active");

    if (window.backgroundMusic) {
      window.backgroundMusic.pause();
    }
  }
}
